//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import {
  IGetHashtagsUsageResponse,
  IInstagramUsedHashtag,
} from '@apis/instagram-hashtags-usage/instagramHashtagsUsageApiModels';
import { deserializeHashtagUsageDates } from '@apis/instagram-hashtags-usage/instagramHashtagsUsageUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const INSTAGRAM_HASHTAGS_USAGE_BASE_URL = '/api/v1/hashtags-usage';
const INSTAGRAM_HASHTAGS_USAGE_V2_BASE_URL = '/api/v2/hashtags-usage';

const hashtagsUsageFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `${INSTAGRAM_HASHTAGS_USAGE_BASE_URL}${endpoint}`,
    init,
  );
};

const hashtagsUsageMultiHandleFetch = (
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `${INSTAGRAM_HASHTAGS_USAGE_V2_BASE_URL}`,
    init,
  );
};

/**
 * Retrieve the current usage of hashtags according to the
 * recently searched hashtags Instagram graph API endpoint.
 * /api/v1/hashtags-usage
 *
 * @returns An object with a data array containing all currently used hashtags as a promise
 */
export async function getHashtagsUsage(
  businessAccountId: string,
  init?: RequestInit,
): Promise<IInstagramUsedHashtag[]> {
  const params = makeParams({ businessAccountId });
  const res = await hashtagsUsageFetch(`?${params}`, {
    ...init,
    method: 'GET',
  });
  const response = await res.json<IGetHashtagsUsageResponse>(
    deserializeHashtagUsageDates,
  );
  return response.data;
}

/**
 * Retrieve the current usage of hashtags across all business accounts for the client.
 * This endpoint consolidates hashtag usage data across multiple connected Instagram accounts.
 *
 * GET /api/v2/hashtags-usage
 */
export async function getHashtagsUsageForClient(
  init?: RequestInit,
): Promise<IInstagramUsedHashtag[]> {
  const res = await hashtagsUsageMultiHandleFetch({
    ...init,
    method: 'GET',
  });
  const response = await res.json<IGetHashtagsUsageResponse>(
    deserializeHashtagUsageDates,
  );
  return response.data;
}
